// ============================================================
// EDIT INSPECTION MODAL COMPONENT
// File: src/components/EditInspectionModal.js
// Allows editing of existing inspection records with audit logging
// ============================================================

import React, { useState, useEffect } from 'react'
import { supabase } from '../supabaseClient'
import { useAgents } from '../hooks/useAgents'
import { useOrganization } from '../hooks/useOrganization'
import { logAuditEvent, AUDIT_ACTIONS } from '../utils/auditLog'
import imageCompression from 'browser-image-compression'
import { useTheme } from '../ThemeContext'
import { SignedImg } from '../utils/photoUrls'

export default function EditInspectionModal({ inspection, establishment, onClose, onSuccess }) {
  const { colors, darkMode } = useTheme()
  const { organization } = useOrganization()

  const mPrimary    = darkMode ? '#c8daf5'  : '#1a2a3e'
  const mSecondary  = darkMode ? '#7aa0c8'  : '#4a6080'
  const mAccent     = darkMode ? '#7aa0c8'  : '#3d6494'
  const mRowBorder  = darkMode ? 'rgba(90,127,173,0.12)' : 'rgba(61,100,148,0.12)'
  const mTheadBg    = darkMode ? 'rgba(90,127,173,0.08)' : 'rgba(61,100,148,0.05)'
  const mInputBg    = darkMode ? 'rgba(20,36,58,0.6)'    : 'rgba(255,255,255,0.9)'
  const mGlassBg    = darkMode ? 'rgba(20,36,58,0.92)'   : 'rgba(255,255,255,0.97)'
  const { agents } = useAgents({ activeOnly: true, role: ['inspector', 'admin'], showErrorToast: false })

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)
  const [sendEmailOnSave, setSendEmailOnSave] = useState(false)
  const [photos, setPhotos] = useState([])
  const [existingPhotos, setExistingPhotos] = useState(inspection.photo_urls || [])
  const [uploadingPhotos, setUploadingPhotos] = useState(false)
  const [photoError, setPhotoError] = useState('')
  const [compressingPhotos, setCompressingPhotos] = useState(false)

  // Form fields - initialized from inspection data
  const [inspectionDate, setInspectionDate] = useState(inspection.inspection_date || '')
  const [selectedAgent, setSelectedAgent] = useState(inspection.inspector_id || '')

  // NC ABC Permits
  const [mixedBeverage, setMixedBeverage] = useState(inspection.mixed_beverage || 'yes')
  const [beerOn, setBeerOn] = useState(inspection.beer_on || 'yes')
  const [beerOff, setBeerOff] = useState(inspection.beer_off || 'yes')
  const [unfortifiedWine, setUnfortifiedWine] = useState(inspection.unfortified_wine || 'yes')
  const [fortifiedWine, setFortifiedWine] = useState(inspection.fortified_wine || 'yes')
  const [otherStatePermits, setOtherStatePermits] = useState(inspection.other_state_permits || '')

  // Local Licenses
  const [countyLicense, setCountyLicense] = useState(inspection.county_license || 'yes')
  const [cityLicense, setCityLicense] = useState(inspection.city_license || 'yes')

  // Records
  const [alcoholRecords, setAlcoholRecords] = useState(inspection.alcohol_records || 'in_compliance')
  const [financialRecords, setFinancialRecords] = useState(inspection.financial_records || 'in_compliance')

  // Premises
  const [storageArea, setStorageArea] = useState(inspection.storage_area || 'in_compliance')
  const [advertising, setAdvertising] = useState(inspection.advertising || 'in_compliance')
  const [cleanliness, setCleanliness] = useState(inspection.cleanliness || 'in_compliance')

  // Additional fields
  const [comments, setComments] = useState(inspection.comments || '')
  const [disposition, setDisposition] = useState(inspection.disposition || 'in_compliance')
  const [personInCharge, setPersonInCharge] = useState(inspection.person_in_charge || '')
  const [email, setEmail] = useState(inspection.email || '')

  // Mobile detection
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768)

  // Section expansion states (for mobile)
  const [expandedSections, setExpandedSections] = useState({
    permits: true,
    licenses: false,
    records: false,
    premises: false,
    additional: false
  })

  // Violations - parse from comments if they exist
  const [selectedViolations, setSelectedViolations] = useState([])
  const [customViolation, setCustomViolation] = useState('')
  const commonViolations = [
    'Sell to Underage',
    'Sell to Intoxicated',
    'Deface Before Empty',
    'Fail to Deface',
    'Fail to Dispose',
    'Fail to Post Permits',
    'Fail to Purchase from Wholesale',
    'Happy Hour Violation',
    'After Hours Violation',
    'Illegal Drugs',
    'Possess Nontaxpaid Liquor',
    'Tax Stamp Missing'
  ]

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768)
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }))
  }

  const handlePhotoSelect = async (e) => {
    const files = Array.from(e.target.files)
    const validFiles = files.filter(file => {
      const isImage = file.type.startsWith('image/')
      if (!isImage) {
        setPhotoError(`${file.name} is not an image file`)
        return false
      }
      return true
    })

    if (validFiles.length === 0) return

    setCompressingPhotos(true)
    setPhotoError('')

    try {
      const compressionOptions = {
        maxSizeMB: 1,
        maxWidthOrHeight: 1920,
        useWebWorker: true,
        fileType: 'image/jpeg'
      }

      const compressedPhotos = []
      for (const file of validFiles) {
        try {
          const compressedFile = await imageCompression(file, compressionOptions)
          compressedPhotos.push({
            file: compressedFile,
            preview: URL.createObjectURL(compressedFile),
            id: Math.random().toString(36).substr(2, 9)
          })
        } catch (err) {
          console.error(`Error compressing ${file.name}:`, err)
          setPhotoError(`Failed to compress ${file.name}`)
        }
      }

      setPhotos(prev => [...prev, ...compressedPhotos])
    } catch (error) {
      console.error('Error processing photos:', error)
      setPhotoError('Failed to process photos')
    } finally {
      setCompressingPhotos(false)
      e.target.value = ''
    }
  }

  const removeNewPhoto = (photoId) => {
    setPhotos(prev => {
      const photo = prev.find(p => p.id === photoId)
      if (photo) URL.revokeObjectURL(photo.preview)
      return prev.filter(p => p.id !== photoId)
    })
  }

  const removeExistingPhoto = (url) => {
    setExistingPhotos(prev => prev.filter(p => p !== url))
  }

  async function uploadPhotos() {
    if (photos.length === 0) return []
    setUploadingPhotos(true)
    const uploadedUrls = []
    for (const photo of photos) {
      try {
        const fileExt = photo.file.name.split('.').pop()
        const fileName = `${inspection.id}_${photo.id}.${fileExt}`
        const filePath = `inspection-photos/${fileName}`
        const { error: uploadError } = await supabase.storage
          .from('inspection-photos')
          .upload(filePath, photo.file)
        if (uploadError) throw uploadError
        const { data: { publicUrl } } = supabase.storage
          .from('inspection-photos')
          .getPublicUrl(filePath)
        uploadedUrls.push(publicUrl)
      } catch (err) {
        console.error('Error uploading photo:', err)
        setPhotoError(`Failed to upload ${photo.file.name}`)
      }
    }
    setUploadingPhotos(false)
    return uploadedUrls
  }

  const handleViolationToggle = (violation) => {
    setSelectedViolations(prev => {
      if (prev.includes(violation)) {
        return prev.filter(v => v !== violation)
      } else {
        return [...prev, violation]
      }
    })
  }

  // Helper to track what changed
  const getChanges = (oldData, newData) => {
    const changes = {}
    const fieldLabels = {
      inspection_date: 'Inspection Date',
      inspector_id: 'Agent',
      mixed_beverage: 'Mixed Beverage',
      beer_on: 'Beer On-Premises',
      beer_off: 'Beer Off-Premises',
      unfortified_wine: 'Unfortified Wine',
      fortified_wine: 'Fortified Wine',
      other_state_permits: 'Other State Permits',
      county_license: 'County License',
      city_license: 'City License',
      alcohol_records: 'Alcohol Records',
      financial_records: 'Financial Records',
      storage_area: 'Storage Area',
      advertising: 'Advertising',
      cleanliness: 'Cleanliness',
      comments: 'Comments',
      disposition: 'Disposition',
      person_in_charge: 'Person in Charge',
      email: 'Email',
      photo_urls: 'Photos'
    }

    for (const key of Object.keys(newData)) {
      const oldVal = oldData[key]
      const newVal = newData[key]

      // Compare arrays (like photo_urls)
      if (Array.isArray(newVal) || Array.isArray(oldVal)) {
        const oldArr = oldVal || []
        const newArr = newVal || []
        if (JSON.stringify(oldArr) !== JSON.stringify(newArr)) {
          changes[fieldLabels[key] || key] = {
            from: `${oldArr.length} photos`,
            to: `${newArr.length} photos`
          }
        }
      } else if (oldVal !== newVal) {
        changes[fieldLabels[key] || key] = {
          from: oldVal || '(empty)',
          to: newVal || '(empty)'
        }
      }
    }

    return changes
  }

  async function sendInspectionEmail(updatedInspection) {
    const recipientEmails = (updatedInspection.email || '')
      .split(',').map(e => e.trim()).filter(Boolean)
    if (recipientEmails.length === 0) return
    const agentRecord = agents.find(a => a.id === updatedInspection.inspector_id)
    if (!agentRecord) return
    await Promise.all(
      recipientEmails.map(recipientEmail =>
        supabase.functions.invoke('send-inspection-email', {
          body: {
            inspection: updatedInspection,
            establishment,
            agentName: `${agentRecord.first_name} ${agentRecord.last_name}`,
            recipientEmail,
            orgName: organization?.name
          }
        })
      )
    )
  }

  async function handleSubmit(e) {
    e.preventDefault()

    try {
      setLoading(true)
      setError('')

      // Format comments with violations if disposition is violation
      let finalComments = comments.trim()
      if (disposition === 'violation' && (selectedViolations.length > 0 || customViolation.trim())) {
        const allViolations = [...selectedViolations]
        if (customViolation.trim()) {
          allViolations.push(customViolation.trim())
        }
        const violationsText = 'VIOLATIONS:\n' + allViolations.map(v => `• ${v}`).join('\n')
        // Check if violations are already in comments to avoid duplication
        if (!finalComments.includes('VIOLATIONS:')) {
          if (finalComments) {
            finalComments = violationsText + '\n\nAdditional Details:\n' + finalComments
          } else {
            finalComments = violationsText
          }
        }
      }

      // Upload new photos if any
      const newPhotoUrls = await uploadPhotos()
      const allPhotoUrls = [...existingPhotos, ...newPhotoUrls]

      const updatedData = {
        inspection_date: inspectionDate,
        inspector_id: selectedAgent,
        mixed_beverage: mixedBeverage,
        beer_on: beerOn,
        beer_off: beerOff,
        unfortified_wine: unfortifiedWine,
        fortified_wine: fortifiedWine,
        other_state_permits: otherStatePermits.trim() || null,
        county_license: countyLicense,
        city_license: cityLicense,
        alcohol_records: alcoholRecords,
        financial_records: financialRecords,
        storage_area: storageArea,
        advertising: advertising,
        cleanliness: cleanliness,
        comments: finalComments || null,
        disposition: disposition,
        person_in_charge: personInCharge.trim() || null,
        email: email.trim() || null,
        photo_urls: allPhotoUrls.length > 0 ? allPhotoUrls : null
      }

      // Get the changes for audit log
      const changes = getChanges(inspection, updatedData)

      // Update the inspection
      const { error: updateError } = await supabase
        .from('inspections')
        .update(updatedData)
        .eq('id', inspection.id)

      if (updateError) throw updateError

      // Log the edit to audit log
      if (Object.keys(changes).length > 0) {
        await logAuditEvent(AUDIT_ACTIONS.INSPECTION_UPDATED, {
          inspection_id: inspection.id,
          establishment_id: establishment.id,
          establishment_name: establishment.trade_name,
          establishment_mxb: establishment.mxb_number,
          inspection_date: inspectionDate,
          changes: changes
        })
      }

      // Send email only if explicitly requested via the resend button
      if (sendEmailOnSave && updatedData.email) {
        sendInspectionEmail({ ...inspection, ...updatedData }).catch(err =>
          console.error('Email send failed (non-critical):', err)
        )
      }

      setSuccess(true)
      setTimeout(() => {
        if (onSuccess) onSuccess()
        if (onClose) onClose()
      }, 1500)

    } catch (error) {
      console.error('Error updating inspection:', error)
      setError(error.message || 'Failed to update inspection')
    } finally {
      setLoading(false)
    }
  }

  const CollapsibleSection = ({ title, sectionKey, bgColor, borderColor, titleColor, children }) => {
    const isExpanded = !isMobile || expandedSections[sectionKey]

    if (!isMobile) {
      return (
        <div style={{ ...sectionStyle, backgroundColor: bgColor, border: `2px solid ${borderColor}` }}>
          <h3 style={{ marginTop: 0, marginBottom: '1.25rem', color: titleColor, fontSize: '1.125rem', fontWeight: '700' }}>
            {title}
          </h3>
          {children}
        </div>
      )
    }

    return (
      <div style={{
        ...sectionStyle,
        backgroundColor: bgColor,
        border: `2px solid ${borderColor}`,
        overflow: 'hidden'
      }}>
        <div
          onClick={() => toggleSection(sectionKey)}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            cursor: 'pointer',
            marginBottom: isExpanded ? '1.25rem' : 0,
            userSelect: 'none'
          }}
        >
          <h3 style={{ margin: 0, color: titleColor, fontSize: '1.125rem', fontWeight: '700' }}>
            {title}
          </h3>
          <div style={{
            fontSize: '1.25rem',
            color: titleColor,
            transition: 'transform 0.2s',
            transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)'
          }}>
            >
          </div>
        </div>
        {isExpanded && (
          <div style={{ animation: 'fadeIn 0.2s ease-out' }}>
            {children}
          </div>
        )}
      </div>
    )
  }

  const RadioGroup = ({ label, value, onChange, options }) => {
    return (
      <div style={{ marginBottom: '1rem' }}>
        {label && (
          <label style={{ display: 'block', marginBottom: '0.6rem', fontWeight: '700', fontSize: '1rem', color: darkMode ? '#ffffff' : '#000000', textTransform: 'uppercase', letterSpacing: '0.04em', textAlign: 'center' }}>
            {label}
          </label>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', width: '100%' }}>
          {options.map(option => (
            <label key={option.value} style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              padding: '0.65rem 1.4rem',
              borderRadius: '9999px',
              backgroundColor: value === option.value ? mAccent : mTheadBg,
              transition: 'all 0.15s',
              boxShadow: value === option.value ? '0 2px 8px rgba(61,100,148,0.3)' : 'none'
            }}>
              <input
                type="radio"
                value={option.value}
                checked={value === option.value}
                onChange={(e) => onChange(e.target.value)}
                style={{ display: 'none' }}
              />
              <span style={{ fontSize: '1rem', fontWeight: '500', color: value === option.value ? 'white' : mSecondary, whiteSpace: 'nowrap' }}>
                {option.label}
              </span>
            </label>
          ))}
        </div>
      </div>
    )
  }

  if (success) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1001
      }}>
        <div style={{
          backgroundColor: darkMode ? 'rgba(6,78,59,0.3)' : '#d1fae5',
          color: darkMode ? '#6ee7b7' : '#065f46',
          padding: '2rem',
          borderRadius: '12px',
          textAlign: 'center',
          maxWidth: '400px',
          border: '1px solid rgba(6,78,59,0.2)'
        }}>
          <h3 style={{ margin: '0 0 0.5rem 0' }}>Inspection Updated Successfully!</h3>
          <p style={{ margin: 0 }}>Changes have been saved and logged.{sendEmailOnSave && email.trim() ? ' Email resent to permittee.' : ''}</p>
        </div>
      </div>
    )
  }

  const inputStyle = {
    width: '100%',
    padding: '0.75rem',
    border: `1px solid ${mRowBorder}`,
    borderRadius: '10px',
    fontSize: '1rem',
    backgroundColor: mInputBg,
    color: mPrimary,
    transition: 'all 0.2s',
    boxSizing: 'border-box'
  }

  const sectionStyle = {
    padding: '1rem',
    borderRadius: '12px',
    marginBottom: '1.5rem'
  }

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1001,
      padding: '0.5rem'
    }}>
      <div style={{
        backgroundColor: mGlassBg,
        padding: '1rem',
        paddingBottom: '2rem',
        borderRadius: '16px',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        maxWidth: '900px',
        width: '100%',
        maxHeight: '95vh',
        overflowY: 'auto',
        border: `1px solid ${mRowBorder}`
      }}>
        <style>
          {`
            input[type="text"]:focus,
            input[type="email"]:focus,
            input[type="date"]:focus,
            select:focus,
            textarea:focus {
              outline: none;
              border-color: #2563eb !important;
              box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1) !important;
            }
            @media (max-width: 768px) {
              input, select, textarea {
                min-height: 44px;
                font-size: 16px !important;
              }
            }
          `}
        </style>

        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.5rem',
          paddingBottom: '0.75rem',
          borderBottom: `1px solid ${mRowBorder}`
        }}>
          <div>
            <div style={{ fontSize: '0.7rem', fontWeight: '600', letterSpacing: '0.12em', textTransform: 'uppercase', color: mAccent, marginBottom: '0.3rem' }}>Edit Inspection</div>
            <h2 style={{ margin: 0, fontSize: 'clamp(1.1rem, 4vw, 1.5rem)', fontWeight: '700', color: mPrimary, lineHeight: 1.2 }}>
              {establishment.trade_name}
            </h2>
            <span style={{ fontSize: '0.875rem', color: mSecondary, display: 'block', marginTop: '0.25rem' }}>
              {establishment.mxb_number}
            </span>
          </div>
          <button onClick={onClose} style={{
            background: mTheadBg,
            border: `1px solid ${mRowBorder}`,
            fontSize: '1.25rem',
            cursor: 'pointer',
            color: mSecondary,
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>×</button>
        </div>

        {error && (
          <div style={{
            backgroundColor: '#fef2f2',
            color: '#991b1b',
            padding: '1rem',
            borderRadius: '12px',
            marginBottom: '1.5rem',
            border: '1px solid #fecaca'
          }}>
            <strong>Error:</strong> {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '2rem' }}>
            <label style={{ display: 'block', marginBottom: '0.75rem', fontWeight: '600', fontSize: '0.875rem', color: mPrimary }}>
              Inspection Date *
            </label>
            <input
              type="date"
              value={inspectionDate}
              onChange={(e) => setInspectionDate(e.target.value)}
              required
              style={inputStyle}
            />
          </div>

          <div style={{ marginBottom: '2rem' }}>
            <label style={{ display: 'block', marginBottom: '0.75rem', fontWeight: '600', fontSize: '0.875rem', color: mPrimary }}>
              Agent *
            </label>
            <select
              value={selectedAgent}
              onChange={(e) => setSelectedAgent(e.target.value)}
              required
              style={{ ...inputStyle, cursor: 'pointer' }}
            >
              <option value="">Select an agent...</option>
              {agents.map(agent => (
                <option key={agent.id} value={agent.id}>
                  {agent.first_name} {agent.last_name}
                </option>
              ))}
            </select>
          </div>

          <CollapsibleSection
            title="NC ABC Permits"
            sectionKey="permits"
            bgColor={mTheadBg}
            borderColor={mRowBorder}
            titleColor={mAccent}
          >
            <RadioGroup label="Mixed Beverage" value={mixedBeverage} onChange={setMixedBeverage}
              options={[{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }]} />
            <RadioGroup label="Beer On-Premises" value={beerOn} onChange={setBeerOn}
              options={[{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }]} />
            <RadioGroup label="Beer Off-Premises" value={beerOff} onChange={setBeerOff}
              options={[{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }]} />
            <RadioGroup label="Unfortified Wine" value={unfortifiedWine} onChange={setUnfortifiedWine}
              options={[{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }]} />
            <RadioGroup label="Fortified Wine" value={fortifiedWine} onChange={setFortifiedWine}
              options={[{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }]} />
            <div style={{ marginBottom: '0' }}>
              <label style={{ display: 'block', marginBottom: '0.75rem', fontWeight: '600', fontSize: '0.875rem', color: mPrimary }}>
                Other State Permits
              </label>
              <input
                type="text"
                value={otherStatePermits}
                onChange={(e) => setOtherStatePermits(e.target.value)}
                placeholder="Enter any other state permits..."
                style={inputStyle}
              />
            </div>
          </CollapsibleSection>

          <CollapsibleSection
            title="Local Licenses"
            sectionKey="licenses"
            bgColor={mTheadBg}
            borderColor={mRowBorder}
            titleColor={mAccent}
          >
            <RadioGroup label="County License" value={countyLicense} onChange={setCountyLicense}
              options={[{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }, { value: 'n/a', label: 'N/A' }]} />
            <RadioGroup label="City License" value={cityLicense} onChange={setCityLicense}
              options={[{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }, { value: 'n/a', label: 'N/A' }]} />
          </CollapsibleSection>

          <CollapsibleSection
            title="Records"
            sectionKey="records"
            bgColor={mTheadBg}
            borderColor={mRowBorder}
            titleColor={mAccent}
          >
            <RadioGroup label="Alcohol Records" value={alcoholRecords} onChange={setAlcoholRecords}
              options={[
                { value: 'in_compliance', label: 'In Compliance' },
                { value: 'needs_attention', label: 'Needs Attention' },
                { value: 'not_in_compliance', label: 'Not in Compliance' }
              ]} />
            <RadioGroup label="Financial Records" value={financialRecords} onChange={setFinancialRecords}
              options={[
                { value: 'in_compliance', label: 'In Compliance' },
                { value: 'needs_attention', label: 'Needs Attention' },
                { value: 'not_in_compliance', label: 'Not in Compliance' }
              ]} />
          </CollapsibleSection>

          <CollapsibleSection
            title="Premises"
            sectionKey="premises"
            bgColor={mTheadBg}
            borderColor={mRowBorder}
            titleColor={mAccent}
          >
            <RadioGroup label="Storage Area" value={storageArea} onChange={setStorageArea}
              options={[
                { value: 'in_compliance', label: 'In Compliance' },
                { value: 'needs_attention', label: 'Needs Attention' },
                { value: 'not_in_compliance', label: 'Not in Compliance' }
              ]} />
            <RadioGroup label="Advertising" value={advertising} onChange={setAdvertising}
              options={[
                { value: 'in_compliance', label: 'In Compliance' },
                { value: 'needs_attention', label: 'Needs Attention' },
                { value: 'not_in_compliance', label: 'Not in Compliance' }
              ]} />
            <RadioGroup label="Cleanliness" value={cleanliness} onChange={setCleanliness}
              options={[
                { value: 'in_compliance', label: 'In Compliance' },
                { value: 'needs_attention', label: 'Needs Attention' },
                { value: 'not_in_compliance', label: 'Not in Compliance' }
              ]} />
          </CollapsibleSection>

          <div style={{ marginBottom: '2rem' }}>
            <label style={{ display: 'block', marginBottom: '0.75rem', fontWeight: '600', fontSize: '0.875rem', color: mPrimary }}>
              Comments
            </label>
            <textarea
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              rows="4"
              placeholder="Enter any comments or observations..."
              style={{ ...inputStyle, fontFamily: 'inherit', resize: 'vertical' }}
            />
          </div>

          <div style={{ marginBottom: '2rem' }}>
            <label style={{ display: 'block', marginBottom: '0.75rem', fontWeight: '600', fontSize: '0.875rem', color: mPrimary }}>
              Disposition *
            </label>
            <RadioGroup label="" value={disposition} onChange={setDisposition}
              options={[
                { value: 'in_compliance', label: 'In Compliance' },
                { value: 'needs_attention', label: 'Needs Attention' },
                { value: 'warning', label: 'Warning' },
                { value: 'violation', label: 'Violation' }
              ]} />
          </div>

          {/* Violation Checkboxes */}
          {disposition === 'violation' && (
            <div style={{
              marginBottom: '2rem',
              padding: '1rem',
              backgroundColor: darkMode ? 'rgba(220,38,38,0.1)' : '#fef2f2',
              border: `1px solid ${darkMode ? 'rgba(220,38,38,0.3)' : '#fecaca'}`,
              borderRadius: '12px'
            }}>
              <label style={{ display: 'block', marginBottom: '0.75rem', fontWeight: '600', fontSize: '0.875rem', color: darkMode ? '#fca5a5' : '#991b1b' }}>
                Select Violations:
              </label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {commonViolations.map(violation => (
                  <label
                    key={violation}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      cursor: 'pointer',
                      padding: '0.75rem',
                      backgroundColor: selectedViolations.includes(violation) ? (darkMode ? 'rgba(220,38,38,0.2)' : '#fee2e2') : mTheadBg,
                      border: selectedViolations.includes(violation) ? '1px solid #dc2626' : `1px solid ${mRowBorder}`,
                      borderRadius: '8px'
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={selectedViolations.includes(violation)}
                      onChange={() => handleViolationToggle(violation)}
                      style={{ marginRight: '0.75rem', cursor: 'pointer', width: '18px', height: '18px' }}
                    />
                    <span style={{
                      fontSize: '0.875rem',
                      fontWeight: '500',
                      color: selectedViolations.includes(violation) ? (darkMode ? '#fca5a5' : '#991b1b') : mPrimary
                    }}>
                      {violation}
                    </span>
                  </label>
                ))}
              </div>

              <div style={{ marginTop: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', fontSize: '0.875rem', color: darkMode ? '#fca5a5' : '#991b1b' }}>
                  Other Violation (specify):
                </label>
                <input
                  type="text"
                  value={customViolation}
                  onChange={(e) => setCustomViolation(e.target.value)}
                  placeholder="Enter custom violation..."
                  style={inputStyle}
                />
              </div>
            </div>
          )}

          <div style={{ marginBottom: '2rem' }}>
            <label style={{ display: 'block', marginBottom: '0.75rem', fontWeight: '600', fontSize: '0.875rem', color: mPrimary }}>
              Person in Charge
            </label>
            <input
              type="text"
              value={personInCharge}
              onChange={(e) => setPersonInCharge(e.target.value)}
              placeholder="Name of person in charge..."
              style={inputStyle}
            />
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.75rem', fontWeight: '600', fontSize: '0.875rem', color: mPrimary }}>
              Email
            </label>
            <input
              type="text"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email addresses (comma-separated)..."
              style={inputStyle}
            />
          </div>

          {/* Existing Photos */}
          {existingPhotos.length > 0 && (
            <div style={{ marginBottom: '2rem' }}>
              <label style={{ display: 'block', marginBottom: '0.75rem', fontWeight: '600', fontSize: '0.875rem', color: mPrimary }}>
                Existing Photos
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '1rem' }}>
                {existingPhotos.map((url, index) => (
                  <div key={index} style={{ position: 'relative', borderRadius: '12px', overflow: 'hidden', border: `2px solid ${colors.border.primary}` }}>
                    <SignedImg src={url} alt={`Inspection ${index + 1}`} style={{ width: '100%', height: '120px', objectFit: 'cover' }} />
                    <button
                      type="button"
                      onClick={() => removeExistingPhoto(url)}
                      style={{
                        position: 'absolute',
                        top: '6px',
                        right: '6px',
                        backgroundColor: 'rgba(220, 38, 38, 0.95)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '50%',
                        width: '28px',
                        height: '28px',
                        cursor: 'pointer',
                        fontSize: '1rem',
                        fontWeight: 'bold',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >x</button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Add New Photos */}
          <div style={{ marginBottom: '2rem' }}>
            <label style={{ display: 'block', marginBottom: '0.75rem', fontWeight: '600', fontSize: '0.875rem', color: mPrimary }}>
              Add Photos
            </label>
            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
              <input type="file" accept="image/*" capture="environment" onChange={handlePhotoSelect} style={{ display: 'none' }} id="edit-camera-capture" />
              <label htmlFor="edit-camera-capture" style={{
                display: 'inline-block',
                padding: '0.75rem 1.5rem',
                backgroundColor: '#3b82f6',
                border: '2px solid #2563eb',
                borderRadius: '10px',
                cursor: 'pointer',
                fontSize: '0.875rem',
                fontWeight: '600',
                color: 'white'
              }}>Take Photo</label>

              <input type="file" accept="image/*" multiple onChange={handlePhotoSelect} style={{ display: 'none' }} id="edit-photo-upload" />
              <label htmlFor="edit-photo-upload" style={{
                display: 'inline-block',
                padding: '0.75rem 1.5rem',
                backgroundColor: colors.bg.secondary,
                border: `2px dashed ${colors.border.primary}`,
                borderRadius: '10px',
                cursor: 'pointer',
                fontSize: '0.875rem',
                fontWeight: '600',
                color: colors.text.primary
              }}>Choose Photos</label>
            </div>

            {compressingPhotos && (
              <div style={{
                padding: '0.75rem',
                backgroundColor: mTheadBg,
                color: mAccent,
                borderRadius: '10px',
                fontSize: '0.875rem'
              }}>
                Compressing photos...
              </div>
            )}

            {photoError && (
              <div style={{
                padding: '0.75rem',
                backgroundColor: '#fef2f2',
                color: '#991b1b',
                borderRadius: '10px',
                fontSize: '0.875rem'
              }}>{photoError}</div>
            )}

            {photos.length > 0 && (
              <div style={{ marginTop: '1rem', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '1rem' }}>
                {photos.map(photo => (
                  <div key={photo.id} style={{ position: 'relative', borderRadius: '12px', overflow: 'hidden', border: '2px solid #10b981' }}>
                    <img src={photo.preview} alt="New" style={{ width: '100%', height: '120px', objectFit: 'cover' }} />
                    <div style={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      backgroundColor: '#10b981',
                      color: 'white',
                      fontSize: '0.6rem',
                      padding: '2px 4px',
                      textAlign: 'center'
                    }}>NEW</div>
                    <button
                      type="button"
                      onClick={() => removeNewPhoto(photo.id)}
                      style={{
                        position: 'absolute',
                        top: '6px',
                        right: '6px',
                        backgroundColor: 'rgba(220, 38, 38, 0.95)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '50%',
                        width: '28px',
                        height: '28px',
                        cursor: 'pointer',
                        fontSize: '1rem',
                        fontWeight: 'bold',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >x</button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div style={{
            display: 'flex',
            gap: '1rem',
            justifyContent: 'flex-end',
            flexWrap: 'wrap',
            marginTop: '2.5rem',
            paddingTop: '1.5rem',
            borderTop: `1px solid ${mRowBorder}`
          }}>
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              style={{
                padding: '0.875rem 1.75rem',
                backgroundColor: mTheadBg,
                color: mSecondary,
                border: `1px solid ${mRowBorder}`,
                borderRadius: '9999px',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontSize: '1rem',
                fontWeight: '600'
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              onClick={() => setSendEmailOnSave(false)}
              disabled={loading || uploadingPhotos || compressingPhotos}
              style={{
                padding: '0.875rem 1.75rem',
                backgroundColor: loading || uploadingPhotos || compressingPhotos ? mTheadBg : mAccent,
                color: loading || uploadingPhotos || compressingPhotos ? mSecondary : 'white',
                border: 'none',
                borderRadius: '9999px',
                cursor: loading || uploadingPhotos || compressingPhotos ? 'not-allowed' : 'pointer',
                fontSize: '1rem',
                fontWeight: '600',
                boxShadow: loading || uploadingPhotos || compressingPhotos ? 'none' : '0 2px 8px rgba(61,100,148,0.3)'
              }}
            >
              {compressingPhotos ? 'Compressing...' : uploadingPhotos ? 'Uploading...' : loading ? 'Saving...' : 'Save Changes'}
            </button>
            <button
              type="submit"
              onClick={() => setSendEmailOnSave(true)}
              disabled={loading || uploadingPhotos || compressingPhotos || !email.trim()}
              title={!email.trim() ? 'No email address on file' : ''}
              style={{
                padding: '0.875rem 1.75rem',
                backgroundColor: loading || uploadingPhotos || compressingPhotos || !email.trim() ? mTheadBg : '#2563eb',
                color: loading || uploadingPhotos || compressingPhotos || !email.trim() ? mSecondary : 'white',
                border: 'none',
                borderRadius: '9999px',
                cursor: loading || uploadingPhotos || compressingPhotos || !email.trim() ? 'not-allowed' : 'pointer',
                fontSize: '1rem',
                fontWeight: '600',
                boxShadow: loading || uploadingPhotos || compressingPhotos || !email.trim() ? 'none' : '0 2px 8px rgba(37,99,235,0.3)'
              }}
            >
              {loading && sendEmailOnSave ? 'Saving...' : 'Save & Resend Email'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
